import type * as Stitches from '@stitches/react';
import { styled } from '@gbm-tech/styles';

const SidebarContainerSM: Stitches.CSS = {
  '> header': {
    button: {
      right: '-1.6rem',
    },
  },
};

const SidebarContainerMD: Stitches.CSS = {
  '> header': {
    button: {
      right: '-2.4rem',
    },
  },
};

const SidebarContainerLG: Stitches.CSS = {
  display: 'flex',
  flexDirection: 'column',

  position: 'sticky',
  top: 0,
  left: 0,

  width: '100%',
  maxWidth: '8rem',
  zIndex: 900,

  '> header': {
    '.logo': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',

      '.image': {
        '&.minimal': {
          width: '100%',
          height: '4rem',
          display: 'initial',
        },

        '&.normal': {
          display: 'none',
        },
      },
    },

    button: {
      display: 'none',
    },
  },
};

const SidebarContainerXXL: Stitches.CSS = {
  maxWidth: '32rem',
  display: 'flex',

  '> header': {
    '.logo': {
      '.image': {
        '&.minimal': {
          display: 'none',
        },

        '&.visible': {
          display: 'flex',
        },
      },
    },

    button: {
      display: 'flex',
    },
  },
};

const SidebarContainerXXLNotIsOpenVariant: Stitches.CSS = {
  width: '100%',
  maxWidth: '8rem',
};

const SidebarContainerOpenStyle: Stitches.CSS = {
  '> header': {
    justifyContent: 'start',
  },
};

const SidebarContainerMinimalStyle: Stitches.CSS = {
  '> header': {
    justifyContent: 'center',
  },
};

const SidebarContainerMinimalStyleLG: Stitches.CSS = {
  display: 'flex',
  flexDirection: 'column',

  position: 'sticky',
  top: 0,
  left: 0,

  width: '100%',
  maxWidth: '8rem',
  zIndex: 900,
};

export const SidebarContainer = styled('aside', {
  size: '100%',
  maxWidth: '80vw',
  maxHeight: '100vh',

  flexDirection: 'column',
  alignItems: 'center',

  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 900,

  borderRight: '1px solid #dae0e7',
  backgroundColor: 'white',

  transition: '0.3s max-width',

  '> header': {
    position: 'relative',

    width: '100%',
    height: '8.8rem',
    padding: '2rem 1.6rem',

    display: 'flex',
    alignItems: 'center',

    '.logo': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: '8px',
      marginBottom: '16px',

      '.image': {
        display: 'none',

        '&.minimal': {
          width: '100%',
          height: '4rem',
          marginTop: '-4px',
        },

        '&.visible': {
          display: 'flex',
        },
      },
    },

    button: {
      position: 'absolute',
      zIndex: 99,

      size: '3.2rem',

      alignItems: 'center',
      justifyContent: 'center',

      borderRadius: '4px',
      border: '1px solid #dae0e7',
      backgroundColor: 'white',

      transition: 'background-color 0.2s',

      svg: {
        size: '2rem',

        color: '#404a59',
      },
    },
  },

  variants: {
    isOpen: {
      true: {
        ...SidebarContainerOpenStyle,

        display: 'flex',

        header: {
          button: {
            svg: {
              transform: 'rotate(180deg)',
            },
          },
        },
      },

      false: {
        ...SidebarContainerMinimalStyle,

        display: 'none',

        '@lg': {
          ...SidebarContainerMinimalStyleLG,
        },

        '@xxl': { ...SidebarContainerXXLNotIsOpenVariant },
      },
    },
  },

  '@sm': {
    ...SidebarContainerSM,
  },

  '@md': {
    ...SidebarContainerMD,
  },

  '@lg': {
    ...SidebarContainerLG,
  },

  '@xxl': {
    ...SidebarContainerXXL,
  },
});

export const SidebarMenuContainer = styled('nav', {
  size: '100%',
  maxHeight: 'calc(100vh - 8.8rem)',

  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '0.16rem',
    backgroundColor: '$gray50',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '$gray100',
  },
});

export const SidebarMenuActionsContainer = styled('nav', {
  width: '100%',
  padding: '0.8rem',

  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.8rem',

  borderTop: '1px solid $gray100',
});

const SidebarActionItemHover: Stitches.CSS = {
  borderColor: '#dae0e7',
  backgroundColor: '#eceef2',

  '.icon': {
    borderColor: '#dae0e7',
    color: '#404a59',
    backgroundColor: '#eceef2',
  },

  '> span': {
    color: '#404a59',
  },

  '.arrow': {
    borderColor: '$blue200',
  },
};

export const SidebarActionItem = styled('button', {
  height: '4.8rem',

  display: 'flex',
  alignItems: 'center',
  gap: '0.6rem',

  border: '1px solid #dae0e7',

  borderRadius: '100px',
  backgroundColor: 'white',

  transition: 'all ease-in-out 0.4s',

  '.icon': {
    size: '4.8rem',
    padding: '1.2rem',

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    color: '#404a59',
    border: '1px solid  #dae0e7',
    borderRadius: '100px',

    transition: 'all ease-in-out 0.4s',

    svg: {
      size: '100%',
    },

    img: {
      size: '100%',
    },
  },

  '> span': {
    flex: 1,

    textAlign: 'left',
    fontSize: '1.4rem',
    lineHeight: '2.4rem',
    fontWeight: 500,

    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',

    color: '$gray400',
  },

  '&:hover': { ...SidebarActionItemHover },

  variants: {
    isSidebarOpen: {
      true: {
        width: '100%',

        borderColor: '$gray300',

        '> span': {
          display: 'hidden',
        },
      },

      false: {
        width: '4.8rem',

        borderColor: 'transparent',
        borderRight: 0,

        '> span': {
          display: 'none',
        },
      },
    },
  },
});
